import {SlctOption, SlctProps} from 'module/Slct/type/SlctType'
import {useSelector} from 'react-redux'
import {selMemoMap} from 'module/Memo/selector/MemoSelector'
import {useMemo} from 'react'
import {MemoParams} from 'module/Memo/hook/useMemoDq'
import {log} from 'common/log'
import {Memorizable} from 'module/Memo/type/Memorizable'
import {ByValueCache} from 'module/Slct/type/ByValueCache'

function noFilter<T>(options: T[]) {
	return options
}

interface Props<ModelType extends object | void, ValueType extends string | number> extends Pick<SlctProps<ModelType, ValueType>, 'value' | 'filterOptions'> {
	memoKey?: MemoParams['memoKey']
	optionsUnfiltered: SlctOption<ModelType, ValueType>[]
	valueObj: SlctOption<ModelType, ValueType> | null
	inputValue: string
	byValue: ByValueCache<ValueType>
	filterOnInputValueEqualsValue: boolean
}

const EMPTY_MEMO: Memorizable[] = []
const EMPTY_OPTS: SlctOption<never, never>[] = []

export function useSlctOptions<ModelType extends object | void, ValueType extends string | number>({
	memoKey,
	optionsUnfiltered,
	filterOptions,
	value,
	valueObj,
	inputValue,
	byValue,
	filterOnInputValueEqualsValue,
}: Props<ModelType, ValueType>) {
	const memoOptions = (useSelector(selMemoMap)?.[memoKey || ''] || EMPTY_MEMO) as SlctOption<ModelType, ValueType>[]
	return useMemo(() => {
		if (!optionsUnfiltered.length) return {options: EMPTY_OPTS as SlctOption<ModelType, ValueType>[], valueIndex: -1}
		const valueStr = value || value === 0 ? value + '' : null
		const iv = inputValue.trim()
		const ivUpper = iv.replace(/^0+/, '').toUpperCase()
		let opts: SlctOption<ModelType, ValueType>[] = optionsUnfiltered
		let sort = false
		const inputValueEqualsValue = valueStr === iv
		const filterActive = filterOptions && iv && (!inputValueEqualsValue || filterOnInputValueEqualsValue)
		const filterOpts = filterActive ? filterOptions : noFilter
		let memoOptionsVisible = filterOpts(memoOptions, iv).map(m => ({...m, ...byValue[m.value as ValueType], memo: true, active: m.value == value}))
		opts = opts.flatMap((o) => {
			if (o.sortnr) {
				sort = true
			}
			if (o.hidden) {
				memoOptionsVisible = memoOptionsVisible.filter(x => x.value !== o.value)
				return []
			}
			if (!o.value && o.value !== 0) {
				// informational options untouched (i.e. "Weitere Einträge...")
				// marks and splits up options to categories anr returns all others
				return [o]
			}
			const memo = memoOptions.some(x => x.value == o.value)
			o = {...o, memo}
			if (valueObj && value == o.value) {
				o = {...o, active: true}
				if (filterActive) sort = true
			} else if (filterActive && (o.value + '').replace(/^0+/, '').toUpperCase() === ivUpper) {
				o = {...o, looseMatch: true}
				sort = true
			}
			return !memo ? [o] : []
		})
		// filter by input value
		opts = filterOpts(opts, iv)
		if (sort) {
			opts = opts.sort((a, b) => {
				if (a.active && !b.active) return -1
				if (b.active && !a.active) return 1
				if (a.looseMatch && !b.looseMatch) return -1
				if (b.looseMatch && !a.looseMatch) return 1
				// sortnr comes as string from server, but has to get converted to number for correct comparison
				if (!a.sortnr && !b.sortnr) return 0
				if (!a.sortnr) return -1
				if (!b.sortnr) return 1
				return +a.sortnr - +b.sortnr
			})
		}
		if (memoOptionsVisible.find(x => x.value == value) && opts[0]?.value == value) opts = opts.slice(1)
		opts = [...memoOptionsVisible, ...opts]
		const valueIndex = opts.findIndex(x => x.active)
		if (valueObj && valueIndex < 0 && (!inputValue || inputValue == value)) {
			// add missing value, but only when search is not active
			opts.unshift({...valueObj, active: true, memo: !!memoOptions.find(m => m.value == value)})
		}
		log.trace('Slct useSlctOptions', opts, valueIndex)
		return {options: opts, valueIndex}
	}, [optionsUnfiltered, filterOptions, value, valueObj, inputValue, memoOptions, byValue])
}